import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Boxes, Bot, Timer, Ticket, LineChart, Maximize, PhoneCall, ClipboardCheck, Wrench, GraduationCap, ArrowRight, Calculator } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { StatsBar } from "@/components/StatsBar";
import { SectionHead } from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";
import { RequestForm } from "@/components/RequestForm";
import { AIVenueAnalyzer } from "@/components/AIVenueAnalyzer";
import { InvestorBrochure } from "@/components/InvestorBrochure";
import { Seo } from "@/components/Seo";
import { IMAGES } from "@/assets";

const benefitIcons = [Boxes, Bot, Timer, Ticket, LineChart, Maximize];
const stepIcons = [PhoneCall, ClipboardCheck, Wrench, GraduationCap];

export default function FranchisingPage() {
  const { t, lang } = useLang();
  const p = t.franchisingPage;

  return (
    <>
      <Seo
        path="/franchising"
        lang={lang}
        image={IMAGES.franchisingHero}
        title={{
          it: "Franchising RISORICE® — Apri il tuo store automatizzato | Investimento e ROI",
          en: "RISORICE® Franchise — Open your automated store | Investment & ROI",
          ru: "Франшиза RISORICE® — Откройте автоматизированную точку | Инвестиции и ROI",
        }[lang]}
        description={{
          it: "Diventa partner RISORICE®: format automatizzato, spazio di soli 30/40 mq, investimento contenuto e ROI stimato. Analizza la tua location con l'AI e richiedi informazioni.",
          en: "Become a RISORICE® partner: automated format, just 30/40 sqm, low investment and estimated ROI. Analyze your location with AI and request information.",
          ru: "Станьте партнёром RISORICE®: автоматизированный формат, всего 30/40 м², небольшие вложения и оценочный ROI. Проанализируйте локацию с ИИ и запросите информацию.",
        }[lang]}
      />
      {/* Hero — dark full-bleed */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-ink" data-testid="franchising-hero">
        <img src={IMAGES.franchisingHero} alt="RISORICE franchising" className="absolute inset-0 w-full h-full object-cover" data-testid="franchising-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
        <div className="relative mx-auto max-w-[1280px] w-full px-5 md:px-8 pb-16 md:pb-20 pt-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="pill bg-white/90 text-tricolor-green">{p.kicker}</span>
            <h1 className="mt-6 font-display font-extrabold tracking-tight text-5xl md:text-6xl leading-[0.95] text-white max-w-2xl">{p.title}</h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/85 leading-relaxed">{p.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#richiedi" className="btn-primary" data-testid="franchising-cta-form">{p.formTitle} <ArrowRight size={18} /></a>
              <Link to="/simulatore" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white text-sm font-semibold px-7 py-3.5 hover:bg-white hover:text-ink transition-colors duration-200" data-testid="franchising-cta-sim"><Calculator size={18} /> {t.nav.simulator}</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-10 md:py-14 bg-tricolor-green/[0.06] border-b border-line" data-testid="franchising-stats-section">
        <div className="mx-auto max-w-[1280px] px-5 md:px-8">
          <StatsBar variant="franchising" items={p.stats} />
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-surface" data-testid="franchising-benefits">
        <div className="mx-auto max-w-[1280px] px-5 md:px-8">
          <SectionHead label={p.benefitsTitle} title={p.benefitsTitle} subtitle={p.benefitsSub} center />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {p.benefits.map((b, i) => {
              const Icon = benefitIcons[i];
              return (
                <Reveal key={i} delay={(i % 3) * 0.08}>
                  <div className="card p-7 h-full hover:-translate-y-1 hover:shadow-[0_16px_40px_-20px_rgba(0,0,0,0.16)]" data-testid={`franchising-benefit-${i}`}>
                    <span className="w-12 h-12 rounded-2xl bg-tricolor-green/10 text-tricolor-green flex items-center justify-center"><Icon size={24} /></span>
                    <h3 className="mt-4 font-display font-bold text-lg text-ink">{b.title}</h3>
                    <p className="mt-2 text-sm text-muted leading-relaxed">{b.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Steps — dark */}
      <section className="py-20 md:py-28 bg-ink text-white" data-testid="franchising-steps">
        <div className="mx-auto max-w-[1280px] px-5 md:px-8">
          <div className="text-center mb-14">
            <h2 className="font-display font-extrabold tracking-tight text-3xl md:text-5xl text-white">{p.stepsTitle}</h2>
            <p className="mt-3 text-white/60">{"Dal primo contatto all'apertura del tuo store"}</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {p.steps.map((st, i) => {
              const Icon = stepIcons[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="rounded-3xl border border-white/10 bg-white/[0.05] p-7 h-full" data-testid={`franchising-step-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="w-12 h-12 rounded-2xl bg-tricolor-green/20 text-tricolor-green flex items-center justify-center"><Icon size={22} /></span>
                      <span className="font-display font-extrabold text-4xl text-white/15">{st.n}</span>
                    </div>
                    <h3 className="mt-5 font-display font-bold text-lg text-white">{st.title}</h3>
                    <p className="mt-2 text-sm text-white/60 leading-relaxed">{st.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Simulator CTA — red banner */}
      <section className="py-16 md:py-20 bg-white" data-testid="franchising-sim-cta">
        <div className="mx-auto max-w-[1280px] px-5 md:px-8">
          <div className="rounded-[2rem] bg-tricolor-green p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6 shadow-[0_20px_50px_-20px_rgba(230,0,0,0.5)]">
            <div>
              <h3 className="font-display font-bold text-2xl md:text-3xl text-white">{p.simCtaTitle}</h3>
              <p className="mt-2 text-white/85 max-w-2xl">{p.simCtaBody}</p>
            </div>
            <Link to="/simulatore" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-tricolor-green text-sm font-bold px-7 py-3.5 flex-shrink-0 hover:bg-white/90 transition-colors duration-200" data-testid="franchising-open-sim"><Calculator size={18} /> {p.simCtaBtn}</Link>
          </div>
        </div>
      </section>

      {/* AI assessment */}
      <AIVenueAnalyzer />

      {/* Investor brochure */}
      <InvestorBrochure />

      {/* Form */}
      <section id="richiedi" className="py-20 md:py-28 bg-white" data-testid="franchising-form-section">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <RequestForm title={p.formTitle} subtitle={p.formSubtitle} />
        </div>
      </section>
    </>
  );
}
